/**
 * Dashboard data-staleness rules.
 *
 * The backend owns the timestamps: it stamps `last_refreshed_at` and computes
 * `next_refresh_at` on create (`POST /agents/dashboards`), on a frequency change
 * (`PATCH /agents/dashboards/:id`) and on a manual refresh
 * (`POST /agents/dashboards/:id/refresh`), and returns both from
 * `GET /agents/dashboards-admin` — in camelCase, see `readStalenessFields`.
 * Requests may use either casing; the aliases are accepted on all three routes.
 * The admin panel picks a frequency and displays what comes back — the
 * fallbacks below exist for rows written before the backend started sending
 * `next_refresh_at`.
 */

/** The only frequencies the picker offers (hours). */
export const REFRESH_FREQUENCY_PRESETS = [
  { value: "", label: "No scheduled refresh" },
  { value: "168", label: "Weekly" },
  { value: "720", label: "Monthly" },
  { value: "2160", label: "Every 3 months" },
  { value: "4380", label: "Every 6 months" },
];

/**
 * Staleness fields in this module's snake_case shape, read from either casing.
 *
 * `GET /agents/dashboards-admin` (and the create/update/refresh responses) send
 * camelCase — `refreshFrequencyHours`, `lastRefreshedAt`, `nextRefreshAt` —
 * while request bodies and everything below use snake_case.
 */
export function readStalenessFields(source) {
  if (!source || typeof source !== "object") {
    return {
      refresh_frequency_hours: null,
      last_refreshed_at: null,
      next_refresh_at: null,
    };
  }
  return {
    refresh_frequency_hours:
      source.refresh_frequency_hours ?? source.refreshFrequencyHours ?? null,
    last_refreshed_at: source.last_refreshed_at ?? source.lastRefreshedAt ?? null,
    next_refresh_at: source.next_refresh_at ?? source.nextRefreshAt ?? null,
  };
}

const asHourString = (hours) => (hours == null || hours === "" ? "" : String(hours));

export const isPresetFrequency = (hours) =>
  REFRESH_FREQUENCY_PRESETS.some((o) => o.value !== "" && o.value === asHourString(hours));

export const formatRefreshFrequency = (hours) => {
  if (!hours) return "—";
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return "—";
  if (h === 168) return "Weekly";
  if (h >= 672 && h <= 744) return "Monthly";
  if (h === 2160) return "Every 3 months";
  if (h === 4380) return "Every 6 months";
  if (h < 24) return h === 1 ? "Every hour" : `Every ${h}h`;
  const days = h / 24;
  if (days < 7) return days === 1 ? "Daily" : `Every ${days}d`;
  const weeks = Math.round(h / 168);
  if (h % 168 === 0 && weeks > 1) return `Every ${weeks} weeks`;
  const months = Math.round(h / 720);
  if (months > 1) return `Every ${months} months`;
  return `Every ${h}h`;
};

/**
 * Presets, plus the dashboard's own frequency when it is not one of them — so
 * editing a dashboard saved with an off-preset frequency shows that frequency
 * instead of silently resetting it on the next save.
 */
export function refreshFrequencyOptions(currentHours) {
  const current = asHourString(currentHours);
  if (!current || isPresetFrequency(current)) return REFRESH_FREQUENCY_PRESETS;
  return [
    ...REFRESH_FREQUENCY_PRESETS,
    { value: current, label: `${formatRefreshFrequency(current)} (current)` },
  ];
}

/**
 * Frequency part of a create/update body. Edit always sends the field, so
 * clearing the schedule reaches the backend (and it clears `next_refresh_at`);
 * create omits it when unset and lets the backend default apply.
 */
export function refreshFrequencyPayload(mode, raw) {
  const hours = raw === "" || raw == null ? null : Number(raw);
  if (hours != null && (!Number.isFinite(hours) || hours <= 0)) {
    return mode === "edit" ? { refresh_frequency_hours: null } : {};
  }
  if (mode === "edit") return { refresh_frequency_hours: hours };
  return hours == null ? {} : { refresh_frequency_hours: hours };
}

/** `next_refresh_at` from the API; last refresh + frequency for older rows. */
export function nextRefreshAt(dashboard) {
  const fields = readStalenessFields(dashboard);
  const given = fields.next_refresh_at;
  if (given) {
    const at = new Date(given);
    if (!Number.isNaN(at.getTime())) return at;
  }
  const hours = Number(fields.refresh_frequency_hours);
  const last = fields.last_refreshed_at;
  if (!last || !Number.isFinite(hours) || hours <= 0) return null;
  const base = new Date(last);
  return Number.isNaN(base.getTime()) ? null : new Date(base.getTime() + hours * 3600000);
}

/** Scheduled but never refreshed, or past its next-due time. Drives the filter. */
export function isDashboardOverdue(dashboard, now = Date.now()) {
  const fields = readStalenessFields(dashboard);
  if (!fields.refresh_frequency_hours) return false;
  if (!fields.last_refreshed_at) return true;
  const next = nextRefreshAt(dashboard);
  return next ? next.getTime() <= now : false;
}

const pad2 = (n) => String(n).padStart(2, "0");

/** ISO (or Date) → `datetime-local` input value in local time; "" when unusable. */
export function toDateTimeInputValue(value) {
  if (!value) return "";
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return "";
  return (
    `${at.getFullYear()}-${pad2(at.getMonth() + 1)}-${pad2(at.getDate())}` +
    `T${pad2(at.getHours())}:${pad2(at.getMinutes())}`
  );
}

/** `datetime-local` input value (local time) → ISO string; null when empty/unusable. */
export function fromDateTimeInputValue(value) {
  if (!value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

const toIso = (value) => {
  if (!value) return null;
  const at = value instanceof Date ? value : new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
};

/** Next due implied by a last-refresh time and a frequency; null when either is missing. */
export function computeNextRefreshAt(lastRefreshedAt, frequencyHours) {
  const hours = Number(frequencyHours);
  const last = toIso(lastRefreshedAt);
  if (!last || !Number.isFinite(hours) || hours <= 0) return null;
  return new Date(new Date(last).getTime() + hours * 3600000).toISOString();
}

/**
 * Timestamp part of a create/update body, for admins recording a refresh they did
 * by hand. A manually entered next due wins; otherwise it is derived from the
 * last refresh plus the frequency. Edit sends both fields so clearing them
 * sticks; create omits what was left blank and lets the backend default apply.
 */
export function stalenessTimestampsPayload(mode, values = {}) {
  const last = toIso(values.lastRefreshedAt);
  const next =
    toIso(values.nextRefreshAt) || computeNextRefreshAt(last, values.frequencyHours);
  if (mode === "edit") return { last_refreshed_at: last, next_refresh_at: next };
  const payload = {};
  if (last) payload.last_refreshed_at = last;
  if (next) payload.next_refresh_at = next;
  return payload;
}

/**
 * Body for `POST /agents/dashboards/:id/refresh`.
 *
 * Every field is optional there: the backend stamps `last_refreshed_at` as now
 * and derives `next_refresh_at` from the frequency. The frequency only rides
 * along so that one picked in the edit form but not yet saved is what next due
 * is derived from — it is omitted when there is no schedule, since sending null
 * would clear the dashboard's frequency.
 */
export function manualRefreshPayload(dashboard) {
  const hours = Number(readStalenessFields(dashboard).refresh_frequency_hours);
  return Number.isFinite(hours) && hours > 0 ? { refreshFrequencyHours: hours } : {};
}

/** Local timestamp, or null when there is nothing (or nothing valid) to show. */
export function formatStalenessTime(value) {
  if (!value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toLocaleString();
}

/**
 * The timestamps a refresh (or save) response echoes back, whatever envelope it
 * uses. Returns null when the response carries none, so the caller re-reads
 * `GET /agents/dashboards-admin` instead of guessing.
 */
export function readRefreshTimestamps(body) {
  const source = body?.dashboard ?? body?.data ?? body;
  if (!source || typeof source !== "object") return null;
  const { last_refreshed_at: last, next_refresh_at: next } = readStalenessFields(source);
  if (!last && !next) return null;
  return { last_refreshed_at: last, next_refresh_at: next };
}
